"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import type { SyncState } from "../types";

const SYNC_INTERVAL_MS = 5 * 60 * 1000;
const DEBOUNCE_MS = 5_000;
const RETRY_DELAYS = [15_000, 30_000, 60_000, 120_000];

export interface SyncSchedulerCallbacks {
  /** Perform the sync. Should throw on auth errors. */
  doSync: () => Promise<{ errors: string[] }>;
  /** Subscribe to visibility/foreground changes. Return unsubscribe function. */
  onVisibilityChange: (callback: () => void) => () => void;
  /** Clear sync cursors and tombstones on logout */
  clearSyncState: () => void | Promise<void>;
}

export interface SyncSchedulerState {
  syncState: SyncState;
  lastSyncAt: string | null;
  error: string | null;
  triggerSync: () => void;
}

export function useSyncScheduler(
  isAuthenticated: boolean,
  callbacks: SyncSchedulerCallbacks,
): SyncSchedulerState {
  const [syncState, setSyncState] = useState<SyncState>("idle");
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const periodicTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const isSyncingRef = useRef(false);
  const initializedRef = useRef(false);

  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const doSyncRef = useRef<(() => Promise<void>) | undefined>(undefined);

  const scheduleRetry = useCallback(() => {
    if (retryCountRef.current >= RETRY_DELAYS.length) return;

    const delay = RETRY_DELAYS[retryCountRef.current];
    retryCountRef.current++;
    retryTimerRef.current = setTimeout(() => {
      if (doSyncRef.current) void doSyncRef.current();
    }, delay);
  }, []);

  const doSync = useCallback(async () => {
    if (!isAuthenticated || isSyncingRef.current) return;

    isSyncingRef.current = true;
    setSyncState("syncing");
    setError(null);

    try {
      const result = await callbacksRef.current.doSync();

      if (result.errors.length > 0) {
        setError(result.errors[0]);
        setSyncState("error");
        scheduleRetry();
      } else {
        setSyncState("idle");
        retryCountRef.current = 0;
      }
      setLastSyncAt(new Date().toISOString());
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sync failed";

      if (message === "Authentication required") {
        setSyncState("idle");
      } else {
        setError(message);
        setSyncState("error");
        scheduleRetry();
      }
    } finally {
      isSyncingRef.current = false;
    }
  }, [isAuthenticated, scheduleRetry]);

  doSyncRef.current = doSync;

  const triggerSync = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => void doSync(), DEBOUNCE_MS);
  }, [doSync]);

  // Sync on login
  useEffect(() => {
    if (isAuthenticated && !initializedRef.current) {
      initializedRef.current = true;
      void doSync();
    }
  }, [isAuthenticated, doSync]);

  // Periodic sync
  useEffect(() => {
    if (!isAuthenticated) {
      if (periodicTimerRef.current) {
        clearInterval(periodicTimerRef.current);
        periodicTimerRef.current = null;
      }
      return;
    }

    periodicTimerRef.current = setInterval(() => void doSync(), SYNC_INTERVAL_MS);

    return () => {
      if (periodicTimerRef.current) {
        clearInterval(periodicTimerRef.current);
        periodicTimerRef.current = null;
      }
    };
  }, [isAuthenticated, doSync]);

  // Visibility/foreground change
  useEffect(() => {
    if (!isAuthenticated) return;

    return callbacksRef.current.onVisibilityChange(() => {
      void doSync();
    });
  }, [isAuthenticated, doSync]);

  // Logout cleanup
  useEffect(() => {
    if (!isAuthenticated && initializedRef.current) {
      initializedRef.current = false;
      void callbacksRef.current.clearSyncState();
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      retryCountRef.current = 0;
      setSyncState("idle");
      setLastSyncAt(null);
      setError(null);
    }
  }, [isAuthenticated]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  return { syncState, lastSyncAt, error, triggerSync };
}
